import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { auth } from "../auth/auth";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import FormTextField, {
  emailValidationPattern,
  passwordValidationPattern,
} from "../components/formComponents/FormTextField";
import { logSignUpEvent } from "../misc/firebase";
import { isErrorWithMessage } from "../misc/isErrorWithMessage";
import ReturnToPasswordLoginButton from "./ReturnToPasswordLoginButton";
import SignInSignUpContainer from "./SignInSignUpContainer";

interface EmailPasswordLoginFormState {
  readonly email: string;
  readonly password: string;
  readonly confirmPassword: string;
}

const EmailPasswordSignUp = () => {
  const { control, handleSubmit } = useForm<EmailPasswordLoginFormState>({
    defaultValues: { email: "", password: "", confirmPassword: "" },
  });
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const enqueueSignUpError = (reason: string, message = "Rejestracja nie powiodła się") =>
    enqueueSnackbar({
      variant: "error",
      message: `${message}. Powód: ${reason}`,
      autoHideDuration: 15000,
    });

  const onSubmit = async (email: string, password: string) => {
    try {
      await auth.signUpWithEmailAndPassword(email, password);
      enqueueSnackbar(`Konto zostało dla adresu ${email} zostało utworzone. Możesz się zalogować.`, {
        variant: "success",
      });
      logSignUpEvent("email_and_password");
      navigate("/login");
    } catch (error) {
      enqueueSignUpError(isErrorWithMessage(error) ? error.message : JSON.stringify(error));
    }
  };

  return (
    <SignInSignUpContainer>
      <Typography
        variant="h4"
        gutterBottom
        color="text.primary"
        sx={{
          fontWeight: "bold",
        }}
      >
        Rejestracja
      </Typography>
      <FormTextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Adres e-mail"
        name="email"
        autoComplete="email"
        autoFocus
        type="email"
        variant="outlined"
        control={control}
        rules={{ required: true, pattern: emailValidationPattern }}
        InputProps={{
          sx: { borderRadius: 2 },
        }}
      />
      <FormTextField
        margin="normal"
        required
        fullWidth
        id="password"
        name="password"
        label="Hasło"
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        variant="outlined"
        control={control}
        rules={{ required: true, pattern: passwordValidationPattern }}
        InputProps={{
          sx: { borderRadius: 2 },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <FormTextField
        margin="normal"
        required
        fullWidth
        id="confirmPassword"
        name="confirmPassword"
        label="Powtórz hasło"
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        variant="outlined"
        control={control}
        rules={{
          required: true,
          pattern: passwordValidationPattern,
          validate: (value, formValues) => (value === formValues.password ? undefined : "Hasła muszą być takie same"),
        }}
        InputProps={{
          sx: { borderRadius: 2 },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ mt: 1, mb: 1 }} />
      <AsyncLoadingButton
        onClick={handleSubmit((s) => onSubmit(s.email, s.password))}
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        sx={{ py: 1.5, mb: 2, fontSize: "1.1rem", borderRadius: 16, boxShadow: "0 4px 12px rgba(53, 87, 161, 0.3)" }}
      >
        Zarejestruj się
      </AsyncLoadingButton>
      <ReturnToPasswordLoginButton onClick={() => navigate("/")} />
    </SignInSignUpContainer>
  );
};

export default EmailPasswordSignUp;
